import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import axiosInstance from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  ArrowLeft, Copy, QrCode, ExternalLink, Download,
  Calendar, Tag, Loader2,
} from 'lucide-react';
import './DeepLinkDetail.css';

const PERIODS = [
  { value: '24h', label: 'Today' },
  { value: '7d',  label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
];

const CHART_COLORS = [
  '#0d7463', '#2563eb', '#7c3aed', '#ea580c', '#ca8a04',
  '#dc2626', '#059669', '#6366f1', '#ec4899', '#78716c',
];
const DEVICE_COLORS = ['#0d7463', '#2563eb', '#ea580c'];

const formatNumber = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K';
  return n?.toString() || '0';
};

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency', currency: 'NGN', minimumFractionDigits: 0,
  }).format(n || 0);

const formatRevenue = (value, currency = '₦') => {
  if (!value || value === 0) return `${currency}0`;
  if (value >= 1_000_000) return `${currency}${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000)     return `${currency}${(value / 1_000).toFixed(1)}K`;
  return `${currency}${value.toLocaleString()}`;
};

const STATUS_PILL = {
  active:   'dld-pill-success',
  expired:  'dld-pill-warn',
  inactive: 'dld-pill-muted',
};

const statusLabel = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export default function DeepLinkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [link, setLink] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [qrCode, setQrCode] = useState(null);

  const fetchDetail = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/deep-links/${id}/analytics?period=${period}`);
      if (res.data.success) {
        setLink(res.data.link);
        setAnalytics(res.data.analytics);
      }
    } catch (err) {
      console.error('Failed to fetch link detail:', err);
      if (err.response?.status === 404) {
        showToast('Deep link not found', 'error');
        navigate('/deep-links');
      }
    }
  }, [id, period, navigate, showToast]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchDetail();
      setLoading(false);
    };
    load();
  }, [fetchDetail]);

  useEffect(() => {
    const fetchQR = async () => {
      try {
        const res = await axiosInstance.get(`/deep-links/${id}/qr`);
        if (res.data.success) setQrCode(res.data.qrCode);
      } catch {
        /* non-critical */
      }
    };
    fetchQR();
  }, [id]);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('Link copied', 'success');
    } catch {
      showToast('Failed to copy', 'error');
    }
  };

  const handleQRDownload = () => {
    if (!qrCode) return;
    const a = document.createElement('a');
    a.href = qrCode;
    a.download = `qr-${link?.shortCode || 'deep-link'}.png`;
    a.click();
    showToast('QR code downloaded', 'success');
  };

  const getStatus = () => {
    if (!link) return 'inactive';
    if (!link.isActive) return 'inactive';
    if (link.expiresAt && new Date(link.expiresAt) < new Date()) return 'expired';
    return 'active';
  };

  /* ── Chart configs ───────────────────────────────────── */
  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', align: 'end', labels: { usePointStyle: true, font: { size: 11 } } },
    },
    scales: {
      x:  { grid: { display: false }, ticks: { font: { size: 10 } } },
      y:  { beginAtZero: true, grid: { color: '#f3f4f6' }, ticks: { font: { size: 10 } }, position: 'left' },
      y1: { beginAtZero: true, grid: { display: false }, ticks: { font: { size: 10 }, callback: (v) => formatRevenue(v) }, position: 'right' },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { padding: 12, usePointStyle: true, font: { size: 11 } } },
    },
    cutout: '65%',
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, grid: { color: '#f3f4f6' }, ticks: { font: { size: 10 } } },
      y: { grid: { display: false }, ticks: { font: { size: 11 } } },
    },
  };

  /* ── Render: states ──────────────────────────────────── */
  if (loading) {
    return (
      <Shell>
        <FeatureGate featureId="marketing.deep_links" showUpgradePrompt>
          <div className="dld-page">
            <div className="dld-loading">
              <Loader2 size={20} className="dld-spin" /> Loading…
            </div>
          </div>
        </FeatureGate>
      </Shell>
    );
  }

  if (!link) {
    return (
      <Shell>
        <FeatureGate featureId="marketing.deep_links" showUpgradePrompt>
          <div className="dld-page">
            <div className="dld-empty">
              <h3 className="dld-empty-title">Deep link not found</h3>
              <p className="dld-empty-desc">It may have been removed.</p>
              <Link to="/deep-links" className="dld-btn dld-btn-secondary">
                <ArrowLeft size={14} /> Back to deep links
              </Link>
            </div>
          </div>
        </FeatureGate>
      </Shell>
    );
  }

  const platformDomain = process.env.REACT_APP_PLATFORM_DOMAIN || 'userevstar.com';
  const shortUrl = link.shortUrl || `https://${platformDomain}/l/${link.customAlias || link.shortCode}`;
  const status = getStatus();

  const clickTrend = analytics?.clickTrend || [];
  const referrers = analytics?.referrers || [];
  const devices = analytics?.devices || [];
  const browsers = analytics?.browsers || [];
  const operatingSystems = analytics?.operatingSystems || [];
  const countries = analytics?.countries || [];
  const conversions = analytics?.conversions || [];

  const maxOS = Math.max(...operatingSystems.map(o => o.clicks), 1);
  const maxReferrer = Math.max(...referrers.map(r => r.clicks), 1);

  const trendChartData = {
    labels: clickTrend.map(d => new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
    datasets: [
      {
        label: 'Total clicks',
        data: clickTrend.map(d => d.clicks),
        borderColor: '#0d7463',
        backgroundColor: 'rgba(13, 116, 99, 0.08)',
        fill: true, tension: 0.4, pointRadius: 2, pointHoverRadius: 5,
      },
      {
        label: 'Unique',
        data: clickTrend.map(d => d.uniqueClicks),
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.05)',
        fill: true, tension: 0.4, pointRadius: 2, pointHoverRadius: 5,
        borderDash: [4, 4],
      },
      {
        label: 'Revenue',
        data: clickTrend.map(d => d.revenue || 0),
        borderColor: '#ca8a04',
        backgroundColor: 'rgba(202, 138, 4, 0.06)',
        fill: true, tension: 0.4, pointRadius: 2, pointHoverRadius: 5,
        yAxisID: 'y1',
      },
    ],
  };

  const referrerChartData = {
    labels: referrers.slice(0, 8).map(r => r.source),
    datasets: [{
      data: referrers.slice(0, 8).map(r => r.clicks),
      backgroundColor: CHART_COLORS.slice(0, 8),
      borderWidth: 0, hoverOffset: 4,
    }],
  };

  const deviceChartData = {
    labels: devices.map(d => d.type?.charAt(0).toUpperCase() + d.type?.slice(1)),
    datasets: [{
      data: devices.map(d => d.clicks),
      backgroundColor: DEVICE_COLORS,
      borderWidth: 0, hoverOffset: 4,
    }],
  };

  const browserChartData = {
    labels: browsers.slice(0, 6).map(b => b.name),
    datasets: [{
      data: browsers.slice(0, 6).map(b => b.clicks),
      backgroundColor: '#2563eb',
      borderRadius: 4,
      barThickness: 20,
    }],
  };

  const countryChartData = {
    labels: countries.slice(0, 8).map(c => c.country),
    datasets: [{
      data: countries.slice(0, 8).map(c => c.clicks),
      backgroundColor: '#0d7463',
      borderRadius: 4,
      barThickness: 20,
    }],
  };

  return (
    <Shell>
      <FeatureGate featureId="marketing.deep_links" showUpgradePrompt>
        <div className="dld-page">

          {/* ── Breadcrumb ──────────────────────────────── */}
          <Link to="/deep-links" className="dld-back">
            <ArrowLeft size={14} /> Back to deep links
          </Link>

          {/* ── Header card ─────────────────────────────── */}
          <header className="dld-header">
            <div className="dld-header-main">
              <div className="dld-header-text">
                <h1 className="dld-title">
                  {link.title || link.destinationUrl}
                  <span className={`dld-pill ${STATUS_PILL[status] || 'dld-pill-muted'}`}>
                    {statusLabel(status)}
                  </span>
                </h1>
                <a
                  href={link.destinationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dld-destination"
                >
                  <ExternalLink size={11} /> {link.destinationUrl}
                </a>
              </div>

              <div className="dld-short-row">
                <code className="dld-short-url">{shortUrl}</code>
                <span className="dld-short-actions">
                  <button
                    type="button"
                    className="dld-icon-btn"
                    title="Copy short link"
                    aria-label="Copy short link"
                    onClick={() => handleCopy(shortUrl)}
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    type="button"
                    className="dld-icon-btn"
                    title="Download QR code"
                    aria-label="Download QR code"
                    onClick={handleQRDownload}
                    disabled={!qrCode}
                  >
                    <QrCode size={14} />
                  </button>
                </span>
              </div>

              <div className="dld-meta">
                {link.campaign && (
                  <span>
                    <Tag size={11} /> Campaign: <strong>{link.campaign}</strong>
                  </span>
                )}
                <span>
                  <Calendar size={11} /> Created: <strong>{new Date(link.createdAt).toLocaleDateString()}</strong>
                </span>
                {link.expiresAt && (
                  <span>
                    <Calendar size={11} /> Expires: <strong>{new Date(link.expiresAt).toLocaleDateString()}</strong>
                  </span>
                )}
              </div>
            </div>

            {qrCode && (
              <div className="dld-qr-aside">
                <img src={qrCode} alt="QR code" className="dld-qr-img" />
                <button
                  type="button"
                  className="dld-qr-download"
                  onClick={handleQRDownload}
                >
                  <Download size={11} /> Download PNG
                </button>
              </div>
            )}
          </header>

          {/* ── Date filter (scrollable on mobile) ──────── */}
          <div className="dld-tabs-wrap">
            <div className="dld-tabs">
              {PERIODS.map(p => (
                <button
                  key={p.value}
                  type="button"
                  className={`dld-tab${period === p.value ? ' is-active' : ''}`}
                  onClick={() => setPeriod(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Stat tiles ──────────────────────────────── */}
          <section className="dld-stat-grid">
            <div className="dld-stat">
              <span className="dld-stat-label">Total clicks</span>
              <span className="dld-stat-value">{formatNumber(link.totalClicks)}</span>
            </div>
            <div className="dld-stat">
              <span className="dld-stat-label">Unique visitors</span>
              <span className="dld-stat-value">{formatNumber(link.uniqueClicks)}</span>
            </div>
            <div className="dld-stat">
              <span className="dld-stat-label">Conversions</span>
              <span className="dld-stat-value dld-stat-value-success">{link.totalConversions}</span>
            </div>
            <div className="dld-stat">
              <span className="dld-stat-label">Revenue</span>
              <span className="dld-stat-value">{formatCurrency(link.totalRevenue)}</span>
            </div>
            <div className="dld-stat">
              <span className="dld-stat-label">Conv. rate</span>
              <span className="dld-stat-value">{analytics?.conversionRate || '0'}%</span>
            </div>
            <div className="dld-stat">
              <span className="dld-stat-label">Avg. order value</span>
              <span className="dld-stat-value">{formatCurrency(analytics?.avgOrderValue || 0)}</span>
            </div>
          </section>

          {/* ── Click timeline ──────────────────────────── */}
          <section className="dld-card">
            <div className="dld-card-head">
              <h2 className="dld-card-title">Click timeline</h2>
            </div>
            <div className="dld-chart-canvas">
              {clickTrend.length > 0 ? (
                <Line data={trendChartData} options={lineOptions} />
              ) : (
                <p className="dld-chart-empty">No click data for this period</p>
              )}
            </div>
          </section>

          {/* ── 2x2 analytics ────────────────────────────── */}
          <div className="dld-grid">
            {/* Traffic sources */}
            <section className="dld-card">
              <div className="dld-card-head">
                <h2 className="dld-card-title">Traffic sources</h2>
              </div>
              <div className="dld-chart-canvas dld-chart-canvas-sm">
                {referrers.length > 0 ? (
                  <Doughnut data={referrerChartData} options={doughnutOptions} />
                ) : (
                  <p className="dld-chart-empty">No referrer data yet</p>
                )}
              </div>
              {referrers.length > 0 && (
                <ul className="dld-bar-list">
                  {referrers.slice(0, 5).map((r, i) => (
                    <li key={i} className="dld-bar-row">
                      <span className="dld-bar-label">{r.source}</span>
                      <div className="dld-bar-track">
                        <div
                          className="dld-bar-fill"
                          style={{ width: `${(r.clicks / maxReferrer) * 100}%`, background: CHART_COLORS[i] }}
                        />
                      </div>
                      <span className="dld-bar-meta">
                        <strong>{formatNumber(r.clicks)}</strong> · {formatRevenue(r.revenue || 0)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Devices */}
            <section className="dld-card">
              <div className="dld-card-head">
                <h2 className="dld-card-title">Devices</h2>
              </div>
              <div className="dld-chart-canvas dld-chart-canvas-sm">
                {devices.length > 0 ? (
                  <Doughnut data={deviceChartData} options={doughnutOptions} />
                ) : (
                  <p className="dld-chart-empty">No device data yet</p>
                )}
              </div>
            </section>

            {/* Browsers + OS */}
            <section className="dld-card">
              <div className="dld-card-head">
                <h2 className="dld-card-title">Browsers</h2>
              </div>
              {browsers.length > 0 ? (
                <div
                  className="dld-chart-canvas"
                  style={{ height: Math.max(180, browsers.slice(0, 6).length * 36) }}
                >
                  <Bar data={browserChartData} options={barOptions} />
                </div>
              ) : (
                <p className="dld-chart-empty">No browser data yet</p>
              )}
              {operatingSystems.length > 0 && (
                <>
                  <div className="dld-card-head" style={{ marginTop: 16 }}>
                    <h2 className="dld-card-title">Operating systems</h2>
                  </div>
                  <ul className="dld-bar-list">
                    {operatingSystems.slice(0, 5).map((o, i) => (
                      <li key={i} className="dld-bar-row">
                        <span className="dld-bar-label">{o.name}</span>
                        <div className="dld-bar-track">
                          <div
                            className="dld-bar-fill"
                            style={{ width: `${(o.clicks / maxOS) * 100}%`, background: '#7c3aed' }}
                          />
                        </div>
                        <span className="dld-bar-meta">
                          <strong>{formatNumber(o.clicks)}</strong> · {formatRevenue(o.revenue || 0)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>

            {/* Top countries */}
            <section className="dld-card">
              <div className="dld-card-head">
                <h2 className="dld-card-title">Top countries</h2>
              </div>
              {countries.length > 0 ? (
                <div
                  className="dld-chart-canvas"
                  style={{ height: Math.max(180, countries.slice(0, 8).length * 36) }}
                >
                  <Bar data={countryChartData} options={barOptions} />
                </div>
              ) : (
                <p className="dld-chart-empty">No geographic data yet</p>
              )}
            </section>
          </div>

          {/* ── Attributed conversions ────────────────────── */}
          {conversions.length > 0 && (
            <section className="dld-card">
              <div className="dld-card-head">
                <h2 className="dld-card-title">
                  Attributed conversions <span className="dld-card-count">({conversions.length})</span>
                </h2>
              </div>
              <ul className="dld-list">
                {conversions.map((c, i) => (
                  <li key={i} className="dld-row">
                    <div className="dld-conv-row">
                      <code className="dld-conv-id">
                        {c.orderId ? `${c.orderId.slice(0, 8)}…` : 'N/A'}
                      </code>
                      <span className="dld-conv-date">
                        {c.convertedAt ? new Date(c.convertedAt).toLocaleDateString() : 'N/A'}
                      </span>
                      <strong className="dld-conv-amount">{formatCurrency(c.value)}</strong>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

        </div>
      </FeatureGate>
    </Shell>
  );
}
